
import React , {useState , useEffect , useRef} from "react";
import LocationAutocomplete from "../../common/LocationAutocomplete";
import { useTheme } from "../../../hooks/useTheme";




const CustomDropdown = ({
  label,
  options,
  value,
  onChange,
  required,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        optionRefs.current = [];
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll to highlighted option
  useEffect(() => {
    if (selectedIndex >= 0 && optionRefs.current[selectedIndex] && isOpen) {
      optionRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex, isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSelectedIndex(-1);
    optionRefs.current = [];
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        } else {
          setSelectedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : 0,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : options.length - 1,
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && selectedIndex >= 0) {
          handleSelect(options[selectedIndex].value);
        } else if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        }
        break;
      case 'Escape':
        if (isOpen) {
          setIsOpen(false);
          setSelectedIndex(-1);
          optionRefs.current = [];
        }
        break;
    }
  };

  return (
    <div className="relative">
      <label
        className={`
    absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1
  `}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div ref={dropdownRef} className="w-full mt-2">
        <button
          type="button"
          className={`w-full px-6 py-3 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-colors duration-200 ${className}`}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span>
            {options.find((opt) => opt.value === value)?.label ||
              'Select an option'}
          </span>
          <svg
            className="dark:text-[#5F6373] text-textSecondary h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        </button>
        {isOpen && (
          <ul
            className="absolute z-10 w-full mt-1 dark:bg-[#0a1733] bg-bgPrimary border border-[#5A4479] rounded-lg shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                className={`px-6 py-2 dark:text-[#CCCCCC] text-textSecondary cursor-pointer transition-colors duration-200 ${
                  index === selectedIndex
                    ? 'dark:bg-[#5A4479] dark:text-white text-textSecondary bg-gray-300'
                    : 'dark:hover:bg-[#2A2A2A] hover:bg-gray-300'
                }`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};




const experienceOptions = [
  { value: "0-0", label: "0 Years (Fresher)" },
  { value: "0-1", label: "0-1 Years (Entry Level)" },
  { value: "1-2", label: "1-2 Years (Junior)" },
  { value: "2-3", label: "2-3 Years (Mid-Junior)" },
  { value: "3-5", label: "3-5 Years (Mid-Level)" },
  { value: "5-7", label: "5-7 Years (Senior)" },
  { value: "7-10", label: "7-10 Years (Lead)" },
  { value: "10-15", label: "10-15 Years (Principal)" },
  { value: "15+", label: "15+ Years (Executive)" },
];



/**
 * Step1 Props:
 *
 * formData                  - { jobRole, minExp, maxExp, location, responsibilities, errors }
 * setFormData               - setter from parent (used for inline textarea onChange)
 * updateFormData            - (updates: object) => void  helper that merges into formData
 *
 * // Job Role autocomplete
 * jobRoleInputRef           - React.RefObject<HTMLInputElement>
 * jobRoleContainerRef       - React.RefObject<HTMLDivElement>
 * suggestionsRef            - React.RefObject<HTMLUListElement>
 * suggestionRefs            - React.MutableRefObject<HTMLLIElement[]>
 * showJobRoleSuggestions    - boolean
 * jobRoleSuggestions        - string[]
 * selectedSuggestionIndex   - number
 * handleJobRoleChangeWithSuggestions - (e: ChangeEvent) => void
 * handleJobRoleKeyDown      - (e: KeyboardEvent) => void
 * handleSuggestionSelect    - (role: string) => void
 *
 * // Experience
 * handleExperienceSelect    - (value: string) => void
 *
 * // JD textarea / file upload
 * fileInputRef              - React.RefObject<HTMLInputElement>
 * isDragging                - boolean
 * isUploadingJD             - boolean
 * isGeneratingJD            - boolean
 * handleDragOver            - (e: DragEvent) => void
 * handleDragLeave           - (e: DragEvent) => void
 * handleDropJD              - (e: DragEvent) => void
 * handleFileInputChange     - (e: ChangeEvent<HTMLInputElement>) => void
 * handleGenerateJDWithAI    - () => Promise<void>
 * onReplaceJDConfirmChange  - (open: boolean) => void  (optional; lets parent disable its own Next)
 *
 * // Navigation
 * isStepOneNextDisabled     - boolean
 * handleAnalyze             - () => void
 * onCancel                  - () => void  (replaces navigate(-1))
 */
const SharedStep1_JobDetails = ({
  // form state
  formData,
  setFormData,
  updateFormData,

  // job role autocomplete
  jobRoleInputRef,
  jobRoleContainerRef,
  suggestionsRef,
  suggestionRefs,
  showJobRoleSuggestions,
  jobRoleSuggestions,
  selectedSuggestionIndex,
  handleJobRoleChangeWithSuggestions,
  handleJobRoleKeyDown,
  handleSuggestionSelect,

  // experience
  handleExperienceSelect,

  // jd textarea / file
  fileInputRef,
  isDragging,
  isUploadingJD,
  isGeneratingJD,
  handleDragOver,
  handleDragLeave,
  handleDropJD,
  handleFileInputChange,
  handleGenerateJDWithAI,
  onReplaceJDConfirmChange,

  // navigation
  isStepOneNextDisabled,
  handleAnalyze,
  onCancel,
  hideNavButtons = false,
}) => {
  const { theme } = useTheme();
  const [showReplaceJDConfirm, setShowReplaceJDConfirm] = useState(false);
  const [justGeneratedJD, setJustGeneratedJD] = useState(false);
  const jdBeforeGenerateRef = useRef(null);

  const setReplaceJDConfirm = (open) => {
    setShowReplaceJDConfirm(open);
    onReplaceJDConfirmChange?.(open);
  };

  const startGenerateJD = () => {
    setReplaceJDConfirm(false);
    setJustGeneratedJD(false);
    jdBeforeGenerateRef.current = formData.responsibilities;
    handleGenerateJDWithAI();
  };

  // Ask before overwriting a JD the user already has
  const onGenerateJDClick = () => {
    if (formData.responsibilities?.trim()) {
      setJustGeneratedJD(false);
      setReplaceJDConfirm(true);
      return;
    }
    startGenerateJD();
  };

  // Show the success note only when a generation actually replaced the text
  useEffect(() => {
    if (isGeneratingJD || jdBeforeGenerateRef.current === null) return;
    if (formData.responsibilities && formData.responsibilities !== jdBeforeGenerateRef.current) {
      setJustGeneratedJD(true);
    }
    jdBeforeGenerateRef.current = null;
  }, [isGeneratingJD, formData.responsibilities]);

  // Don't leave the parent's Next disabled if this step unmounts mid-confirm
  useEffect(() => () => onReplaceJDConfirmChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const isJDBusy = isGeneratingJD || isUploadingJD;

  const inputLabelStyles = `
    absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1
  `;

  const isBasicInfoMissing = !formData.jobRole?.trim() || 
    formData.minExp === "" || 
    formData.maxExp === "" || 
    typeof formData.minExp !== "number" || 
    typeof formData.maxExp !== "number";

  // Inline NavigationButtons (kept local to avoid extra prop drilling)
  const NavigationButtons = ({ onBack, onNext, nextDisabled = false, showBackButton = true, showborderbtn = false }) => (
    <div className="flex justify-between items-center w-full py-4">
      {showBackButton ? (
        <button
          onClick={onBack}
          className={`${showborderbtn && theme !== "dark" ? "border-btn !text-textSecondary" : "button"} flex items-center gap-2 rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200`}
        >
          Back
        </button>
      ) : (
        <div />
      )}
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className={`button px-6 py-3 rounded-full text-white ${nextDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <span>Next</span>
      </button>
    </div>
  );

  return (
    <div className="w-full px-12 space-y-6">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-light dark:text-white text-textPrimary flex items-center space-x-3">
            <span>Let's begin by adding a few details to create your job role</span>
          </h1>
        </div>

        {/* Row: Job Title / Experience / Location */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Job Title */}
          <div className="relative" ref={jobRoleContainerRef}>
            <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              ref={jobRoleInputRef}
              type="text"
              className="w-full px-6 py-3 mt-2 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
              placeholder="e.g., UX Designer, Full Stack Developer"
              value={formData.jobRole}
              onChange={handleJobRoleChangeWithSuggestions}
              onKeyDown={handleJobRoleKeyDown}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="job-role-suggestions"
              aria-activedescendant={
                selectedSuggestionIndex >= 0 ? `suggestion-${selectedSuggestionIndex}` : undefined
              }
              required
            />
            {showJobRoleSuggestions && (
              <ul
                ref={suggestionsRef}
                id="job-role-suggestions"
                role="listbox"
                className="absolute z-10 w-full mt-1 dark:bg-gray-800 bg-bgPrimary border border-[#5A4479] rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {jobRoleSuggestions.map((role, index) => (
                  <li
                    key={role}
                    ref={(el) => (suggestionRefs.current[index] = el)}
                    id={`suggestion-${index}`}
                    role="option"
                    aria-selected={index === selectedSuggestionIndex}
                    className={`px-4 py-2 dark:text-[#CCCCCC] text-textSecondary cursor-pointer ${
                      index === selectedSuggestionIndex
                        ? "dark:bg-gray-700 bg-gray-200"
                        : "dark:hover:bg-gray-700 hover:bg-gray-200"
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionSelect(role)}
                  >
                    {role}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Experience Dropdown */}
          <CustomDropdown
            label="Experience"
            options={experienceOptions}
            value={
              formData.minExp !== "" && formData.maxExp !== ""
                ? formData.maxExp >= 99
                  ? `${formData.minExp}+`
                  : `${formData.minExp}-${formData.maxExp}`
                : ""
            }
            onChange={handleExperienceSelect}
            required
          />

          {/* Location */}
          <div className="relative">
            <label className={`${inputLabelStyles} z-20 dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary`}>
              Location
            </label>
            <LocationAutocomplete
              value={formData.location}
              onChange={(location) => updateFormData({ location })}
              placeholder="e.g. Tokyo, Japan or New York, USA"
              className="mt-2"
            />
          </div>
        </div>

        {/* Job Description */}
        <div className="relative w-full">
          <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textPrimary -top-3 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1">
            Job Description <span className="text-red-400">*</span>
          </label>

          <div className="mt-2">
            <div
              className={`w-full border rounded-lg overflow-hidden transition-colors ${
                formData.errors.responsibilities ? "border-red-500" : "border-[#5A4479]"
              } ${isGeneratingJD ? "bg-[#FAF9FF] dark:bg-white/[0.03]" : "bg-transparent"}`}
            >
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  if (isJDBusy) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDragLeave(e);
                    return;
                  }
                  handleDropJD(e);
                }}
                className={`relative transition-all pt-3 ${
                  isDragging && !isUploadingJD ? "ring-2 ring-blue-400 ring-offset-2" : ""
                }`}
              >
                {/* Textarea */}
                <div className="relative">
                <textarea
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  className={`w-full px-5 py-3 bg-transparent font-normal dark:text-gray-300 text-textSecondary text-sm placeholder-gray-500 placeholder:italic focus:outline-none resize-none [&::-webkit-scrollbar]:hidden ${
                    isJDBusy ? "cursor-not-allowed" : ""
                  } ${isUploadingJD ? "opacity-50" : ""}`}
                  value={formData.responsibilities}
                  disabled={isJDBusy}
                  aria-busy={isJDBusy}
                  placeholder="Insert detailed job description or drop a PDF file here"
                  onChange={(e) => {
                    setJustGeneratedJD(false);
                    const input = e.target.value;
                    const words = input.trim().split(/\s+/).filter(Boolean);
                    const wordCount = words.length;
                    let responsibilitiesError = "";
                    if (input.trim() !== "") {
                      if (wordCount < 50) {
                        responsibilitiesError = `Job description must be at least 50 words`;
                      } 
                      // else if (wordCount > 500) {
                      //   responsibilitiesError = `Job description cannot exceed 500 words`;
                      // }
                    }
                    setFormData((prev) => ({
                      ...prev,
                      responsibilities: input,
                      errors: { ...prev.errors, responsibilities: responsibilitiesError },
                    }));
                  }}
                  rows={12}
                  required
                />

                {/* Generating overlay: skeleton lines over the disabled textarea */}
                {isGeneratingJD && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="absolute inset-0 flex flex-col gap-2.5 px-5 py-3 bg-[#FAF9FF] dark:bg-[#140F1C]"
                  >
                    <div className="flex items-center gap-2 mb-1 text-sm font-medium text-[#5A4BB5] dark:text-[#DAC0FF]">
                      <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                        <path d="M21 12a9 9 0 1 1-9-9" />
                      </svg>
                      Generating job description…
                    </div>
                    {["92%", "84%", "88%", "60%", "30%", "78%", "70%"].map((width, i) => (
                      <div
                        key={i}
                        className={`h-2.5 rounded-full animate-pulse bg-gray-300 dark:bg-gray-600 ${i === 4 ? "mt-2" : ""}`}
                        style={{ width }}
                      />
                    ))}
                  </div>
                )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                {showReplaceJDConfirm ? (
                  /* Inline confirm: replaces the toolbar until the user decides */
                  <div className="px-3 pb-3 pt-1">
                    <div
                      role="alert"
                      className="flex flex-wrap items-center gap-2.5 min-h-[40px] py-1 pl-3 pr-1 rounded-3xl border bg-[#FFF6E8] border-[#F2C77A] dark:bg-amber-900/20 dark:border-amber-700/50"
                    >
                      <svg className="w-4 h-4 flex-shrink-0 text-[#9A5B00] dark:text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3l9.5 17h-19z" />
                        <path d="M12 10v4M12 17.5v.01" />
                      </svg>
                      <span className="flex-1 min-w-[180px] text-sm text-[#6B3F00] dark:text-amber-200">
                        This will replace your current job description.
                      </span>
                      <button
                        type="button"
                        onClick={() => setReplaceJDConfirm(false)}
                        autoFocus
                        className="px-4 py-1.5 rounded-full border text-sm bg-white border-[#C9C4E0] text-textSecondary dark:bg-transparent dark:border-gray-600 dark:text-white"
                      >
                        Keep mine
                      </button>
                      <button
                        type="button"
                        onClick={startGenerateJD}
                        className="button px-4 py-1.5 rounded-full text-sm font-medium text-white"
                      >
                        <span>Replace</span>
                      </button>
                    </div>
                  </div>
                ) : (
                /* Toolbar: Upload + Create JD with AI */
                <div className="py-3 flex justify-between items-center px-4">
                  <div className="flex gap-3 items-center">
                    <div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingJD || isGeneratingJD || isBasicInfoMissing}
                        className={`relative flex items-center gap-2 rounded-full transition-all px-3 py-2 pr-3.5 cursor-pointer
                          ${theme === "dark" ? "secondary-button" : "border-btn"}
                          ${(isUploadingJD || isGeneratingJD || isBasicInfoMissing)
                            ? (theme === "dark" ? "!bg-gray-600 !text-gray-400 !border-gray-600 opacity-60" : "opacity-60 !text-textSecondary")
                            : (theme === "dark" ? "dark:!text-white" : "!text-textSecondary")
                          }`}
                      >
                        {isUploadingJD ? (
                          <>
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="text-sm font-medium">Uploading</span>
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            <span className="text-sm font-medium">Upload</span>
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-gray-500 font-medium text-sm">or</p>

                    {/* Create JD with AI */}
                    <button
                      onClick={onGenerateJDClick}
                      disabled={isGeneratingJD || isUploadingJD || isBasicInfoMissing}
                      className={`flex items-center justify-center min-w-[170px] px-3 py-2 rounded-full text-white text-sm font-medium transition-all cursor-pointer
                        ${(isGeneratingJD || isUploadingJD || isBasicInfoMissing)
                          ? (theme === "dark"
                              ? "!bg-gray-600 !bg-none text-gray-400 opacity-60"
                              : "button opacity-60")
                          : "button"
                        }`}
                    >
                      {isGeneratingJD ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span className="ml-2">Generating…</span>
                        </>
                      ) : (
                        <span>Generate JD with AI</span>
                      )}
                    </button>

                    {justGeneratedJD && !isGeneratingJD && (
                      <span className="flex items-center gap-1 ml-1 text-xs text-[#2E7D57] dark:text-emerald-400">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M5 12l5 5 9-10" />
                        </svg>
                        Job description generated
                      </span>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Validation errors */}
        <div>
          {Object.entries(formData.errors).map(
            ([key, value]) =>
              value && (
                <p key={key} className="text-red-400 text-sm">
                  {value}
                </p>
              )
          )}
        </div>
      </div>

      {!hideNavButtons && (
        <NavigationButtons
          onBack={onCancel}
          onNext={handleAnalyze}
          nextDisabled={isStepOneNextDisabled || isJDBusy || showReplaceJDConfirm}
          showBackButton={false}
          showborderbtn={true}
        />
      )}
    </div>
  );
};

export default SharedStep1_JobDetails;
